import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { StreamChat } from "stream-chat";

// useGlobalChatUnread — the app-shell TOTAL chat-unread count (Design Bible D26:
// a global unread pill on the "Messages"/Inbox nav item, across all lanes;
// source = Stream unread, the same signal the per-lane tab counts read).
//
// This is the ONLY app-wide Stream reader. It is deliberately minimal and
// DEFENSIVE: it reuses the already-persisted Stream token (no refresh storm),
// connects one lightweight read-only client on its OWN dedicated instance (kept
// separate from the inbox page's client so neither owns the other's lifecycle),
// seeds `total_unread_count` from the connect handshake, keeps it live off the
// unread-moving events, and disconnects on unmount. The Header mounts once per
// session, so this is one connection for the app's lifetime. Anything missing
// (no user, no token, no api key) → returns 0 and never connects, so it can
// never break the shell.
//
// Returns a number (0 when unknown). The nav shows a pill only when > 0.
const apiKey = process.env.REACT_APP_STREAM_CHAT_API_KEY;

export const useGlobalChatUnread = () => {
  const user = useSelector((state) => state.auth.user);
  const userId = user?.id?.toString();
  // The Stream token useInbox persisted onto the user (user.streamChatId).
  const token = user?.streamChatId;
  const [unread, setUnread] = useState(0);
  const clientRef = useRef(null);

  useEffect(() => {
    if (!apiKey || !userId || !token) {
      setUnread(0);
      return undefined;
    }

    let cancelled = false;
    let client = null;
    const subs = [];

    const apply = (event) => {
      // Stream stamps total_unread_count on every unread-moving event and on the
      // connection handshake (event.me / own_user). Read whichever is present.
      const n =
        event?.total_unread_count ??
        event?.me?.total_unread_count ??
        event?.own_user?.total_unread_count ??
        client?.user?.total_unread_count;
      if (typeof n === "number" && !cancelled) setUnread(n);
    };

    const connect = async () => {
      try {
        client = new StreamChat(apiKey);
        const health = await client.connectUser({ id: userId }, token);
        if (cancelled) {
          await client.disconnectUser().catch(() => {});
          return;
        }
        apply(health);
        clientRef.current = client;

        const events = [
          "message.new",
          "notification.message_new",
          "notification.mark_read",
          "message.read",
          "notification.added_to_channel",
        ];
        for (const e of events) subs.push(client.on(e, apply));
      } catch (err) {
        // Never surface — the pill just stays hidden. (Logged quietly.)
        if (!cancelled) setUnread(0);
        // eslint-disable-next-line no-console
        console.debug("Global chat unread unavailable:", err?.message || err);
      }
    };

    connect();

    return () => {
      cancelled = true;
      subs.forEach((s) => s?.unsubscribe?.());
      const c = clientRef.current;
      clientRef.current = null;
      if (c) c.disconnectUser().catch(() => {});
    };
  }, [userId, token]);

  return unread;
};

export default useGlobalChatUnread;
